/**
 * 邮箱管理组件 - 简单图标形态
 * 用于通知中心和用户设置页面的邮箱绑定和管理功能
 */

"use client";
import { createLogAdapter } from "@/lib/logger/console-adapter";

import { Mail, Settings as SettingsIcon } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { useToast } from "@/hooks/use-toast";

const uiLog = createLogAdapter("components/notifications/EmailManagement.tsx");

interface UserNotificationInfo {
	email?: string;
	emailVerified?: boolean;
}

interface EmailManagementProps {
	userPrincipal: string;
	className?: string;
}

export default function EmailManagement({
	userPrincipal,
	className = "",
}: EmailManagementProps) {
	const [userInfo, setUserInfo] = useState<UserNotificationInfo>({});
	const [showEmailModal, setShowEmailModal] = useState(false);
	const [email, setEmail] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const { toast } = useToast();

	// 获取用户邮箱信息
	const fetchUserEmailInfo = useCallback(async () => {
		if (!userPrincipal) return;

		setIsLoading(true);
		try {
			// 先尝试从用户API获取邮箱信息
			const userResponse = await fetch(
				`https://api.odin.fun/v1/user/${userPrincipal}`,
			);
			if (userResponse.ok) {
				const userData = await userResponse.json();

				// 再获取通知相关信息
				const notificationResponse = await fetch(
					`/api/notifications/user-info`,
					{
						headers: {
							"X-User-Principal": userPrincipal,
						},
					},
				);

				if (notificationResponse.ok) {
					const notificationData = await notificationResponse.json();
					// 合并用户数据和通知数据
					setUserInfo({
						email: userData.email || notificationData.email,
						emailVerified:
							userData.email_verified || notificationData.emailVerified,
					});
				} else {
					// 如果通知API失败，只使用用户数据
					setUserInfo({
						email: userData.email,
						emailVerified: userData.email_verified || false,
					});
				}
			}
		} catch (error) {
			uiLog.error("获取用户邮箱信息失败:", error);
		} finally {
			setIsLoading(false);
		}
	}, [userPrincipal]);

	// 验证邮箱格式
	const validateEmail = (email: string) => {
		const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return regex.test(email);
	};

	// 省略显示邮箱地址
	const truncateEmail = (email: string) => {
		if (email.length <= 20) return email;
		const [localPart, domain] = email.split("@");
		if (localPart.length > 8) {
			return `${localPart.substring(0, 6)}...@${domain}`;
		}
		return email;
	};

	// 绑定或修改邮箱
	const handleEmailSubmit = async () => {
		if (!validateEmail(email)) {
			toast({
				title: "❌ 请输入有效的邮箱地址",
				variant: "destructive",
			});
			return;
		}

		setIsSubmitting(true);
		try {
			const response = await fetch("/api/notifications/contact-info", {
				method: "PATCH",
				headers: {
					"Content-Type": "application/json",
					"X-User-Principal": userPrincipal,
				},
				body: JSON.stringify({
					contactInfo: {
						email: email,
						locale: "zh-CN",
						timezone: "Asia/Shanghai",
					},
				}),
			});

			if (response.ok) {
				setUserInfo((prev) => ({
					...prev,
					email: email,
					emailVerified: false,
				}));
				setShowEmailModal(false);
				setEmail("");
				toast({
					title: "✅ 邮箱设置成功",
					description: "请查收验证邮件",
				});
				fetchUserEmailInfo();
			} else {
				const error = await response.json();
				toast({
					title: "❌ 设置失败",
					description: error.message || error.error || "请稍后重试",
					variant: "destructive",
				});
			}
		} catch (error) {
			uiLog.error("设置邮箱失败:", error);
			toast({
				title: "❌ 设置失败",
				description: "网络异常，请稍后重试",
				variant: "destructive",
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	// 打开邮箱设置弹窗
	const openEmailModal = () => {
		setEmail(userInfo.email || "");
		setShowEmailModal(true);
	};

	useEffect(() => {
		fetchUserEmailInfo();
	}, [fetchUserEmailInfo]);

	if (isLoading) {
		return (
			<div className={`flex items-center gap-2 ${className}`}>
				<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-light-primary dark:border-dark-primary"></div>
				<span className="text-sm text-gray-500">加载中...</span>
			</div>
		);
	}

	return (
		<>
			<Button
				variant="ghost"
				size="sm"
				onClick={openEmailModal}
				className={`flex items-center gap-2 h-8 px-2 hover:bg-light-primary/10 dark:hover:bg-dark-primary/10 ${className}`}
			>
				{userInfo.email ? (
					<>
						<SettingsIcon className="w-4 h-4" />
						<span
							className="text-sm truncate max-w-[120px]"
							title={userInfo.email}
						>
							{truncateEmail(userInfo.email)}
						</span>
					</>
				) : (
					<>
						<Mail className="w-4 h-4" />
						<span className="text-sm">绑定邮箱</span>
					</>
				)}
			</Button>

			{/* 邮箱设置弹窗 */}
			<Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							<Mail className="w-5 h-5" />
							{userInfo.email ? "修改邮箱" : "绑定邮箱"}
						</DialogTitle>
					</DialogHeader>

					<div className="space-y-4 py-4">
						{userInfo.email && (
							<div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
								<p className="text-sm text-blue-600 dark:text-blue-400">
									当前邮箱：
									<span className="font-medium">{userInfo.email}</span>
								</p>
								{userInfo.emailVerified ? (
									<p className="text-xs text-green-600 dark:text-green-400 mt-1">
										✅ 已验证
									</p>
								) : (
									<p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
										⏳ 待验证
									</p>
								)}
							</div>
						)}

						<div className="space-y-2">
							<Label htmlFor="email" className="text-sm font-medium">
								邮箱地址
							</Label>
							<Input
								id="email"
								type="email"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								placeholder="请输入邮箱地址"
							/>
						</div>

						<div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
							<div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
								<p className="font-medium">绑定邮箱后，您将可以：</p>
								<ul className="list-disc list-inside ml-2 space-y-0.5">
									<li>接收重要的交易通知</li>
									<li>获取价格预警邮件提醒</li>
									<li>找回账户和安全验证</li>
								</ul>
							</div>
						</div>

						<div className="flex gap-3 pt-2">
							<Button
								variant="outline"
								onClick={() => setShowEmailModal(false)}
								className="flex-1"
							>
								取消
							</Button>
							<Button
								onClick={handleEmailSubmit}
								disabled={isSubmitting || !email}
								className="flex-1"
							>
								{isSubmitting ? (
									<span className="flex items-center gap-2">
										<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
										设置中...
									</span>
								) : (
									"确认设置"
								)}
							</Button>
						</div>
					</div>
				</DialogContent>
			</Dialog>
		</>
	);
}
